import axios from 'axios';
import * as Promise from 'bluebird';
import { I18n } from 'react-redux-i18n-lite';
import * as shared from '../shared';
import routerHistory from '../routerHistory';
import * as CustomTranslate from '../services/translate/customTranslateService';
import * as messageAlert from './messageAlertAction';
import { ACTIVITY_TYPE, PARAMS, ROUTES } from '../const';
import * as bookApi from '../services/api/bookApi';
import * as activitiesApi from '../services/api/activitiesApi';
import {
  API_ACTIVITIES_BY_ACTIVITY_ID,
  API_CLASSROOMS,
  API_CLASSROOMS_BOOK,
  API_CLASSROOMS_BOOK_ORDER,
  API_CLASSROOMS_BOOKS,
  API_CONTENT_ASSIGNATION_BY_GROUP,
  API_CONTENT_ASSIGNATION_BY_GROUPS,
  API_CONTENT_ASSIGNATION_BY_USER,
  API_CONTENT_ASSIGNATION_BY_USERS
} from '../services/api/apiConst';
import { REDUCER } from '../reducers/reducersConst';
import * as _ from 'lodash';
import * as bookService from '../services/domain/bookService';
import { removeNotAllowedCharactersToActivityId } from '../services/domain/bookService';
import { getLogger } from '../services/logger';
import { propagateBookAssignations } from '../services/domain/bookService';
import * as revealActions from './revealActions';
import { DESYNCHRONIZE_ACTIVITY_REVEAL } from '../components/book/desynchronizeBook/DesynchronizeActivityReveal';
import { ANALYTICS_CONTEXT } from '../types/analytics';

const debug = getLogger('actions:bookActions');

/**
 * Saves the expanded unit on the store
 * @param unit
 * @returns {{type: string, unit: number}}
 */
let crossPlatformSpecialization;
export let configure = injectedCrossPlatformSpecialization => {
  crossPlatformSpecialization = injectedCrossPlatformSpecialization;
};

export let setActivities = (activities = []) => {
  return {
    type: REDUCER.ASSIGNMENT_DRAWER.SET_ACTIVITIES,
    activities
  };
};

export let setFetchingBooksActivities = (fetching = false) => {
  return {
    type: REDUCER.ASSIGNMENT_DRAWER.SET_FETCHING_BOOKS_ACTIVITIES,
    fetching
  };
};

export let setSynchronizedClassrooms = (synchronizedClassrooms = []) => {
  return {
    type: REDUCER.BOOK.SET_SYNCHRONIZED_CLASSROOMS,
    synchronizedClassrooms
  };
};

export let setDesynchronizeActivityContext = (desynchronizeActivityContext = null) => {
  return {
    type: REDUCER.BOOK.SET_DESYNCHRONIZE_ACTIVITY_CONTEXT,
    desynchronizeActivityContext
  };
};

export let notifyActivityLinkChanged = (activityIds = null) => {
  const normalizedIds =
    activityIds == null ? null : Array.isArray(activityIds) ? activityIds : [activityIds];

  return {
    type: REDUCER.BOOK.SET_ACTIVITY_LINK_CHANGED,
    activityLinkChanged: normalizedIds ? { activityIds: normalizedIds, ts: Date.now() } : null
  };
};

const getLessonActivityIdsFromUnit = (book, unitActivityId) => {
  if (!book?.content) {
    return [];
  }

  const unit = book.content.find(unitActivity => unitActivity.activityId === unitActivityId);
  if (!unit?.content) {
    return [];
  }

  return unit.content.map(lesson => lesson.activityId).filter(Boolean);
};

export let setBooksData = (books = null) => {
  return {
    type: REDUCER.BOOK.SET_BOOKS_DATA,
    books
  };
};

export let setBookInfo = (book = {}) => {
  return {
    type: REDUCER.BOOK.SET_BOOK_INFO,
    book
  };
};

export let setOpenUnit = (unit = -1) => {
  return (dispatch, getState) => {
    const defaultUnit = bookService.removeNotAllowedCharactersToActivityId(getState().router.location.query.unitId);

    dispatch({
      type: REDUCER.BOOK.SET_OPEN_UNIT,
      unit: defaultUnit || unit
    });
  };
};

/**
 * Sets the selected lesson on the store
 * @param resource
 * @returns {{type: string, resource: {}}}
 */
export const setSelectedResource = (resource = {}) => {
  return {
    type: REDUCER.BOOK.SET_SELECTED_RESOURCE,
    resource
  };
};

/**
 * Sets the selected unit on the store
 * @param selectedUnit
 * @returns {{type: string, selectedUnit: {}}}
 */
export let setSelectedUnit = (selectedUnit = {}) => {
  return {
    type: REDUCER.BOOK.SET_SELECTED_UNIT,
    selectedUnit
  };
};

/**
 * Sets the selected new unit on the store
 * @param position
 * @returns {{type: string, position: number}}
 */
export let setOpenNewUnit = (position = -1) => {
  return {
    type: REDUCER.BOOK.SET_OPEN_NEW_UNIT,
    position
  };
};

/**
 * Sets delete activity reveal data on the store (used by setDeleteUnitData and setDeleteLessonData)
 * @param activity
 * @returns {{type: string, activity: *}}
 */
export let setDeleteActivityData = (activity = null) => {
  return {
    type: REDUCER.BOOK.SET_ACTIVITY_DELETE_DATA,
    activity
  };
};

/**
 * Sets delete activity reveal data on the store for unit
 * @param activity
 * @returns {{type: string, activity: *}}
 */
export let setDeleteUnitData = (activity = null) => {
  activity.type = ACTIVITY_TYPE.UNIT;
  return setDeleteActivityData(activity);
};

/**
 * Sets delete activity reveal data on the store for lesson
 * @param activity
 * @returns {{type: string, activity: *}}
 */
export let setDeleteLessonData = (activity = null) => {
  activity.type = ACTIVITY_TYPE.LESSON;
  return setDeleteActivityData(activity);
};

export let setCopyBookReveal = (copyReveal = null) => {
  return {
    type: REDUCER.BOOK.SET_COPY_REVEAL,
    copyReveal: copyReveal
  };
};

/**
 * Sets the unit which it is been edited on store
 * @param unitEdition
 * @returns {{type: string, unitEdition: number}}
 */
export let setUnitEdition = (unitEdition = -1) => {
  return {
    type: REDUCER.BOOK.SET_UNIT_EDITION,
    unitEdition
  };
};

/**
 * Sets the state of the link lesson modal (open/close)
 * @param isLinkLessonModalOpen
 * @returns {{type: string, isLinkLessonModalOpen: *}}
 */
export let setIsLinkLessonModalOpen = (isLinkLessonModalOpen = null) => {
  return {
    type: REDUCER.BOOK.SET_IS_LINK_LESSON_MODAL_OPEN,
    isLinkLessonModalOpen
  };
};

export let setIsAutoExecLessonModalOpen = (isAutoExecLessonModalOpen = null) => {
  return {
    type: REDUCER.BOOK.SET_IS_AUTO_EXEC_MODAL_OPEN,
    isAutoExecLessonModalOpen
  };
};

export let setNewLessonUnitRef = (unitIdRef = null) => {
  return {
    type: REDUCER.BOOK.SET_NEW_LESSON_UNIT,
    unitIdRef
  };
};

export let setNewLessonUnitId = (unitId = null) => {
  return {
    type: REDUCER.BOOK.SET_NEW_LESSON_UNIT_ID,
    unitId
  };
};

export let setUploadContentStatus = (uploadContentStatus = { percentCompleted: 0, isUploading: false }) => {
  return {
    type: REDUCER.BOOK.SET_UPLOAD_CONTENT_STATUS,
    uploadContentStatus
  };
};

/**
 * Set the bookNames
 * @param books
 * @param language
 * @returns books
 */
let updateBookNames = (books, language) => {
  for (let i = 0; books && i < books.length; i++) {
    if (books[i].name) {
      books[i].bookName = CustomTranslate.translateObject(JSON.parse(books[i].name), language);
    }
  }
  return books;
};

export let setBookName = (bookId, bookName) => {
  return (dispatch, getState) => {
    let state = getState();
    let books = state.book.books;

    for (let i = 0; i < books.length; i++) {
      if (books[i].id === bookId) {
        books[i].bookName = bookName;
        break;
      }
    }

    dispatch(setBooksData(books));
  };
};

export let setCopyRevealData = (copyReveal = {}) => {
  return async dispatch => {
    let classroomsWithImportedBook = await bookApi.getClassesWithBookImported(copyReveal.activityId);
    copyReveal.classroomsWithImportedBook = classroomsWithImportedBook.data;
    dispatch(setCopyBookReveal(copyReveal));
  };
};

/**
 * Action to request the books form a certain classroom
 * @param classroomId
 * @returns {function(*, *)}
 */
export let getBooksData = (classroomId = '') => {
  return async (dispatch, getState) => {
    let store = getState();
    let language = store.account.user.language;
    if (classroomId) {
      try {
        const res = await bookApi.getBooks(classroomId);

        if (!res || !Array.isArray(res.data)) {
          return;
        }

        const books = res.data;
        let bookWithUpdatedNames = updateBookNames(books, language);
        dispatch(setBooksData(bookWithUpdatedNames));
      } catch (error) {
        // Si es un error 404 de classroom, redirigir a home
        if (error.response && error.response.status === 404 && 
            (error.response.data?.code === 'error.classroom.notFound' || 
             error.request?.responseURL?.includes('/classrooms/'))) {
          routerHistory.replace({ pathname: ROUTES.HOME, state: { alertError: 'api.error.classroom.notFound' } });
          return;
        }
        // Para otros errores, re-lanzar
        throw error;
      }
    }
  };
};

export let getBookActivities = (classroomId = '', bookId = '', onlyVisibleActivities = false) => {
  return dispatch => {
    if (classroomId && bookId) {
      dispatch(setFetchingBooksActivities(true));
      return bookApi
        .getActivities(classroomId, bookId, onlyVisibleActivities)
        .then(res => {
          dispatch(setActivities(res.data));
          dispatch(setFetchingBooksActivities(false));
          return res.data;
        })
        .catch(() => {
          dispatch(setActivities());
        });
    }
    debug('Can not get activities from book');
  };
};

export let getSynchronizedClassrooms = (classroomId = '', bookId = '') => {
  return dispatch => {
    if (classroomId && bookId) {
      return bookApi.getSyncClassrooms(classroomId, bookId).then(res => {
        dispatch(setSynchronizedClassrooms(res.data));
      });
    }
  };
};

/**
 * Actions to request the book structure in the context of a certain classroom or section
 */
export let getBookInfo = (classroomId = '', bookId = '') => {
  return async (dispatch, getState) => {
    const store = getState();
    const user = store.account.user;

    if (bookId && user) {
      try {
        const bookRes = await bookApi.getBook(classroomId, bookId);
        const book = bookService.transformCmi5ToBook(bookRes.data, bookId);
        propagateBookAssignations(book);
        dispatch(setBookInfo(book));
        return book;
      } catch (err) {
        debug.error('[getBookInfo] error getting book %s', err);
        
        // Si es un error 404 desde Moodle, redirigir a home
        if (err.response && err.response.status === 404 && store.account.jailed) {
          routerHistory.replace({ pathname: ROUTES.HOME, state: { alertError: 'api.error.classroom.notFound' } });
        } else {
          dispatch(setBookInfo({}));
        }
      }
    }
  };
};

/**
 * Updates the books structure positions (order in units and activities)
 * @param classroomId
 * @param bookId
 * @param parentId
 * @param children
 * @returns {function(*, *)}
 */
export let updateBookStructure = (classroomId = '', bookId = '', parentId = '', children = []) => {
  return dispatch => {
    if (bookId && classroomId && parentId && children) {
      let positions = [];
      children.forEach((child, index) => {
        positions.push({
          activityId: child.activityId,
          position: index
        });
      });

      const url = API_CLASSROOMS_BOOK_ORDER.replace(PARAMS.CLASSROOM, classroomId).replace(PARAMS.BOOK, bookId);
      return axios.put(url, { parentId: parentId, positions: positions });
    }
  };
};

/**
 * Updates the group assignment to a current level of the book structure
 * @param lessonConfiguration
 * @param bookId
 * @param activityId
 * @param newSelection
 * @param classroomId
 * @returns {function(*, *)}
 */
export const assignBookLevel = (
  lessonConfiguration = {},
  newSelection = {},
  activityId = '',
  bookId = '',
  classroomId = '',
  context = ANALYTICS_CONTEXT.BOOK_ASSIGN
) => {
  return dispatch => {
    if (activityId && bookId && classroomId) {
      return unassign(dispatch, lessonConfiguration, classroomId, bookId, activityId).then(() =>
        assign(dispatch, newSelection, classroomId, bookId, activityId, context)
      );
    }
  };
};

function assign(dispatch, selection, classroomId, bookId, activityId, context = ANALYTICS_CONTEXT.BOOK_ASSIGN) {
  if (selection.isGroup && selection.id) {
    const url = API_CONTENT_ASSIGNATION_BY_GROUPS.replace(PARAMS.CLASSROOM, classroomId).replace(PARAMS.BOOK, bookId);
    return axios
      .put(url, { activityId: activityId, groups: [selection.id], context: context })
      .then(() => dispatch(getBookInfo(classroomId, bookId)));
  }
  if (!selection.isGroup) {
    const url = API_CONTENT_ASSIGNATION_BY_USERS.replace(PARAMS.CLASSROOM, classroomId).replace(PARAMS.BOOK, bookId);
    return axios
      .put(url, { activityId: activityId, users: selection.users.map(user => user._id), context: context })
      .then(() => dispatch(getBookInfo(classroomId, bookId)));
  }
  return Promise.resolve();
}

function unassign(dispatch, lessonConfig, classroomId, bookId, activityId) {
  if (lessonConfig.groups.length) {
    return Promise.map(lessonConfig.groups, group => {
      const url = API_CONTENT_ASSIGNATION_BY_GROUP.replace(PARAMS.CLASSROOM, classroomId)
        .replace(PARAMS.BOOK, bookId)
        .replace(PARAMS.GROUP, group)
        .replace(PARAMS.ACTIVITY, activityId);
      return axios.delete(url).then(() => dispatch(getBookInfo(classroomId, bookId)));
    });
  }
  if (lessonConfig.users.length) {
    return Promise.map(lessonConfig.users, user => {
      const url = API_CONTENT_ASSIGNATION_BY_USER.replace(PARAMS.CLASSROOM, classroomId)
        .replace(PARAMS.BOOK, bookId)
        .replace(PARAMS.USER, user)
        .replace(PARAMS.ACTIVITY, activityId);
      return axios.delete(url).then(() => dispatch(getBookInfo(classroomId, bookId)));
    });
  }
  return Promise.resolve();
}

export let addNewUnit = (
  bookId = '',
  classroomId = '',
  position = 0,
  parentActivityId,
  name = I18n.t('unitCard.newUnit'),
  context
) => {
  return (dispatch, getState) => {
    let parentId = _.isNil(parentActivityId) ? getState().book.book.activityId : parentActivityId;
    let payload = {
      language: shared.getLanguageFromStorage(),
      name: name,
      type: 'UNIT',
      position: position
    };
    if (context) {
      payload.context = context;
    }

    return activitiesApi.postActivityTree(classroomId, parentId, payload).then(activityUpdated => {
      dispatch(getBookInfo(classroomId, bookId)).then(() => {
        dispatch(setOpenNewUnit());
        dispatch(setOpenUnit(removeNotAllowedCharactersToActivityId(activityUpdated.data.activityId)));
        dispatch(setSelectedUnit(activityUpdated.data));
      });
      return activityUpdated.data;
    });
  };
};

export const addNewLesson = (lessonName, url, format, idClassroom, unitActivityId, context) => {
  return dispatch => {
    return bookService.addLessonToCMS(lessonName, url, format, idClassroom, unitActivityId, context);
  };
};

/**
 * Updates the data from a unit
 * @param bookId
 * @param classroomId
 * @param name
 * @param activityId
 * @returns {function(*)}
 */
export let updateUnitName = (bookId = '', classroomId = '', activityId = '', name = '') => {
  return dispatch => {
    let payload = {
      language: shared.getLanguageFromStorage(),
      name: name
    };
    return axios.patch(`${API_CLASSROOMS}/${classroomId}/activities?activityId=${activityId}`, payload).then(() => {
      dispatch(getBookInfo(classroomId, bookId));
    });
  };
};

/**
 * Removes an activity from a book used in a classroom
 * @param bookId
 * @param classroomId
 * @param activityId
 * @param context
 * @returns {function(*)}
 */
export let deleteActivity = (bookId = '', classroomId = '', activityId = '', context = null) => {
  return dispatch => {
    let url = `${API_CLASSROOMS}/${classroomId}/activities?activityId=${activityId}`;
    if (context) {
      url += `&context=${encodeURIComponent(context)}`;
    }
    return axios.delete(url).then(() => {
      dispatch(getBookInfo(classroomId, bookId));
    });
  };
};

/**
 * ACTIONS RELATED WITH LAUNCHING SMART CONTENT
 */

export let openResource = openBookConfig => {
  return dispatch => {
    dispatch(crossPlatformSpecialization.openResource(openBookConfig));
    return Promise.resolve();
  };
};

/**
 * Opens a resource link in a new tab
 * @param {string} url
 * @param {boolean} [ownWindow=false]
 */
export let openLink = (url, ownWindow = false) => {
  return (dispatch, getState) => {
    const isAppOnline = getState().connection.status.online;
    if (!isAppOnline) {
      dispatch(messageAlert.error(I18n.t('common.notAvailableOffline')));
    } else if (!url) {
      dispatch(messageAlert.error(I18n.t('generic.notFound')));
    } else {
      const target = ownWindow ? '_self' : '_blank';

      window.open(url, target);
    }
  };
};

/**
 * Sets remove book info in store
 * @param bookToRemove
 * @returns {{type: string, bookToRemove: Object}}
 */
export let setRemoveBookState = (bookToRemove = null) => {
  return {
    type: REDUCER.BOOK.SET_REMOVE_BOOK_INFO,
    bookToRemove
  };
};

/**
 * Sets book to re-download info in store
 * @param bookToReDownload
 * @returns {{type: string, bookToReDownload: Object}}
 */
export let setReDownloadBookState = (bookToReDownload = null) => {
  return {
    type: REDUCER.BOOK.SET_RE_DOWNLOAD_BOOK_INFO,
    bookToReDownload
  };
};

/**
 * Creates a custom book
 * @param classroomId
 * @param name
 * @returns {function(*, *)}
 */
export let createCustomBook = (classroomId = '', name = '') => {
  return dispatch => {
    if (classroomId) {
      let payload = {
        language: shared.getLanguageFromStorage(),
        name: name,
        disabled: true,
        context: ANALYTICS_CONTEXT.BOOK_CREATE
      };
      const url = API_CLASSROOMS_BOOKS.replace(PARAMS.CLASSROOM, classroomId);
      return axios.post(url, payload).then(res => {
        dispatch(getBooksData(classroomId));
        let bookId = res.data.id;
        let activityId = res.data.activityId;

        dispatch(addNewUnit(bookId, classroomId, 0, activityId, undefined, ANALYTICS_CONTEXT.UNIT_CREATE)).then(() => {
          const ROUTE_CLASSROOM_BOOK = `${ROUTES.CLASSROOM_BOOK.replace('_classId_', classroomId)}/${bookId}`;
          routerHistory.push(ROUTE_CLASSROOM_BOOK);
        });
        return res;
      });
    }
  };
};

/**
 * Updates book info
 * @param classroomId
 * @param bookId
 * @param description
 * @returns {function(*, *)}
 */
export let updateBook = (classroomId = '', bookId = -1, name = {}) => {
  return dispatch => {
    if (classroomId) {
      let payload = {
        language: shared.getLanguageFromStorage(),
        name: name
      };
      const url = API_CLASSROOMS_BOOK.replace(PARAMS.CLASSROOM, classroomId).replace(PARAMS.BOOK, bookId);
      return axios.patch(url, payload).then(res => {
        dispatch(getBooksData(classroomId));
        return res;
      });
    }
  };
};

/**
 * Deletes book
 * @param classId
 * @param bookId
 * @param activityId
 * @param context
 * @returns {function(*, *)}
 */
export let deleteBook = (classId = '', bookId = -1, activityId = '', context = null) => {
  return (dispatch, getState) => {
    let store = getState();
    if (classId) {
      let url = API_CLASSROOMS_BOOK.replace(PARAMS.CLASSROOM, classId).replace(PARAMS.BOOK, bookId);
      if (context) {
        url += `?context=${encodeURIComponent(context)}`;
      }
      return axios.delete(url).then(res => {
        dispatch(getBooksData(classId));
        setTimeout(() => {
          dispatch(
            crossPlatformSpecialization.deleteBookPostAction(classId, bookId, activityId, store.account.user._id)
          );
        }, 1000);
        return res;
      });
    }
  };
};

/**
 * Updates the assessable property of activity
 * @param classroomId
 * @param activities
 * @returns {function(*)}
 */
export let updateAssessable = (classroomId = '', activities = []) => {
  return () => {
    return Promise.all(
      activities.map(activity => {
        const url = API_ACTIVITIES_BY_ACTIVITY_ID.replace(PARAMS.CLASSROOM, classroomId).replace(
          PARAMS.ACTIVITY,
          activity.id
        );
        return axios.patch(url, { assessable: activity.assessable });
      })
    );
  };
};

/**
 * Updates de disable property in a book level and its children levels
 * @param classroomId
 * @param bookId
 * @param activityId
 * @param disable
 * @returns {function(*)}
 */
export let updateDisableBookLevel = (classroomId = '', bookId = -1, activityId = '', disable = false, context = null) => {
  return dispatch => {
    let url = API_ACTIVITIES_BY_ACTIVITY_ID.replace(PARAMS.CLASSROOM, classroomId).replace(
      PARAMS.ACTIVITY,
      activityId
    );
    if (context) {
      url += `&context=${encodeURIComponent(context)}`;
    }
    return axios.patch(url, { disabled: disable }).then(() => {
      dispatch(getBookInfo(classroomId, bookId));
      dispatch(getBooksData(classroomId));
    });
  };
};

/**
 * Enables a book level and its children levels
 * @param classroomId
 * @param bookId
 * @param activityId
 * @returns {function(*)}
 */
export let enableBookLevel = (classroomId = '', bookId = -1, activityId = '', context = null) => {
  return dispatch => {
    dispatch(updateDisableBookLevel(classroomId, bookId, activityId, false, context));
  };
};

/**
 * Disables a book level and its children levels
 * @param classroomId
 * @param bookId
 * @param activityId
 * @returns {function(*)}
 */
export let disableBookLevel = (classroomId = '', bookId = -1, activityId = '', context = null) => {
  return dispatch => {
    dispatch(updateDisableBookLevel(classroomId, bookId, activityId, true, context));
  };
};

export function addDocLesson(fileToUpdate, userId, customerId, bookContext) {
  return async dispatch => {
    let handleUploadError = () => {
      dispatch(messageAlert.error(I18n.t('upload.error')));
    };
    let handleSetUploadContentStatus = status => {
      if (status) {
        dispatch(setUploadContentStatus(status));
      }
    };
    try {
      const { uploadedFile, isFileTooBig, isNameValid } = await bookService.addDocLesson(
        fileToUpdate,
        userId,
        customerId,
        bookContext.idClassroom,
        bookContext.unitActivityId,
        handleSetUploadContentStatus
      );
      if (uploadedFile) {
        dispatch(getBookInfo(bookContext.idClassroom, bookContext.idBook));
        dispatch(messageAlert.success(I18n.t('upload.success')));
      } else if (isFileTooBig) {
        dispatch(messageAlert.error(I18n.t('upload.fileTooBig')));
      } else if (!isNameValid) {
        dispatch(messageAlert.error(I18n.t('upload.charactersNotAllow')));
      } else {
        dispatch(messageAlert.error(I18n.t('upload.error')));
      }
    } catch (err) {
      if (err.response && err.response.status === 413) {
        dispatch(setUploadContentStatus());
      } else if (err.message) {
        handleUploadError();
      } else {
        debug.error('[addDocLesson] %s', err);
        dispatch(messageAlert.error(I18n.t('upload.error')));
      }
      console.error(err);
      dispatch(setUploadContentStatus());
    }
  };
}

export function addLinkLesson(lessonName, url, bookContext) {
  return async dispatch => {
    await bookService.addLinkLesson(lessonName, url, bookContext.idClassroom, bookContext.unitActivityId);
    dispatch(getBookInfo(bookContext.idClassroom, bookContext.idBook));
  };
}

export function desynchronizeBook(classroomId, bookId, sectionId, activityId) {
  return async dispatch => {
    await bookApi.unlinkActivity(classroomId, activityId);
    dispatch(getBooksData(classroomId));
    dispatch(notifyActivityLinkChanged([activityId]));
    dispatch(messageAlert.success(I18n.t('bookContainer.bookDesynchronizedSuccess')));
  };
}

export function openDesynchronizeActivityReveal(activityId) {
  return (dispatch, getState) => {
    const { classroom } = getState().classrooms.selectedClassroom;
    const { idBook } = getState().router.params;
    dispatch(
      setDesynchronizeActivityContext({
        activityId,
        classroomId: classroom.id,
        bookId: idBook
      })
    );
    dispatch(revealActions.setOpenReveal(DESYNCHRONIZE_ACTIVITY_REVEAL));
  };
}

export function desynchronizeActivity(classroomId, activityId, bookId) {
  return async (dispatch, getState) => {
    const book = getState().book.book;
    const lessonActivityIds = getLessonActivityIdsFromUnit(book, activityId);
    const affectedActivityIds =
      lessonActivityIds.length > 0 ? [activityId, ...lessonActivityIds] : [activityId];

    await bookApi.unlinkActivity(classroomId, activityId);
    dispatch(setDesynchronizeActivityContext(null));
    dispatch(revealActions.setOpenReveal());
    if (bookId) {
      await dispatch(getBookInfo(classroomId, bookId));
    }
    dispatch(notifyActivityLinkChanged(affectedActivityIds));
    dispatch(messageAlert.success(I18n.t('bookContainer.bookDesynchronizedSuccess')));
  };
}

/**
 * Clones a lesson directly (without modal)
 * @param {Object} lesson - The lesson object to clone
 * @param {string} classroomId - The classroom ID
 * @param {string} bookId - The book ID
 * @param {string} unitActivityId - The parent unit activity ID (where to clone)
 * @returns {function(*, *)}
 */
export let cloneLesson = (lesson, classroomId, bookId, unitActivityId, context) => {
  return async (dispatch, getState) => {
    try {
      if (!lesson || !lesson.activityId) {
        console.error('Lesson and activityId are required');
        dispatch(messageAlert.error(I18n.t('lessonCard.lessonClonedError')));
        return;
      }

      // Llamar al servicio de dominio que maneja la lógica de negocio
      // importFrom es igual que classroomId cuando se clona dentro del mismo libro (no desde otro lugar)
      await bookService.cloneLesson(lesson.activityId, classroomId, classroomId, bookId, unitActivityId, context);
      
      // Actualizar el libro para mostrar la nueva lección clonada
      dispatch(getBookInfo(classroomId, bookId));
      
      // Mostrar mensaje de éxito
      dispatch(messageAlert.success(I18n.t('lessonCard.lessonClonedSuccess')));
      
    } catch (error) {
      console.error('Error al clonar lección:', error);
      const errorMessage = error.response?.data?.message || I18n.t('lessonCard.lessonClonedError');
      dispatch(messageAlert.error(errorMessage));
    }
  };
};

/**
 * Creates a hidden NEAE adaptation lesson from a source lesson and assigns it to the given selection
 * @param {Object} lesson - The source lesson object
 * @param {string} classroomId - The classroom ID
 * @param {string} bookId - The book ID
 * @param {string} unitActivityId - The parent unit activity ID (where to create the adaptation)
 * @param {Object} selection - The selection to assign to ({ isGroup, id } or { users: [...] })
 * @returns {function(*, *)}
 */
export let createNeaeAdaptation = (lesson, classroomId, bookId, unitActivityId, selection = {}) => {
  return async dispatch => {
    try {
      if (!lesson || !lesson.activityId) {
        dispatch(messageAlert.error(I18n.t('lessonCard.adaptationCreatedError')));
        return;
      }

      const payload = {
        classId: classroomId,
        bookId,
        unitActivityId,
        lessonActivityId: lesson.activityId,
        context: ANALYTICS_CONTEXT.NEAE_PANEL
      };

      if (selection.isGroup && selection.id !== undefined) {
        payload.groups = [String(selection.id)];
      } else if (selection.users && selection.users.length) {
        payload.users = selection.users.map(user => user._id);
      } else {
        dispatch(messageAlert.error(I18n.t('lessonCard.adaptationCreatedError')));
        return;
      }

      await bookApi.createNeaeAdaptation(payload);

      dispatch(getBookInfo(classroomId, bookId));

      dispatch(
        messageAlert.success(
          `${I18n.t('lessonCard.adaptationCreatedSuccess')} \n ${I18n.t('rightPanel.neaeAdaptations.info')}`
        )
      );
    } catch (error) {
      const errorMessage = error.response?.data?.message || I18n.t('lessonCard.adaptationCreatedError');
      dispatch(messageAlert.error(errorMessage));
    }
  };
};
